import {
  provideRouter,
  RouteReuseStrategy,
  ROUTES,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { GoogleTagManagerService } from '@b2b/web-google-analytics-lib';
import { WebNoopHttpInterceptor } from '@app/services/interceptors/web-noop-http-interceptor.service';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { effects, reducers } from '@app/store';
import { getWebRoutes } from '@app/bootstrap-apps/web-app/web-app-routing';
import {
  APP_ID,
  APP_INITIALIZER,
  importProvidersFrom,
  inject,
} from '@angular/core';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { StorageUtilLibModule } from '@b2b/storage-util-lib';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { DefaultRouteReuseStrategy } from '@app/services/route-strategy/default-route-reuse-strategy';
import { CustomRouteReuseStrategy } from '@app/services/route-strategy/custom-route-reuse-strategy';
import { AppSettingsService } from '@app/services/global-services/app/app-settings.service';
import { catchError, of, tap } from 'rxjs';
import { AppVersionSetting } from '@app/models/server/app-version.model';
import { APP_BASE_HREF } from '@angular/common';
import { environment } from '@src/environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { HttpLoaderInterceptor } from '@app/services/interceptors/loader-interceptor.service';

export function getWindow() {
  return typeof window !== 'undefined' ? window : null;
}

export function getNavigator() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

const jwtOptionsFactory = (storage: any) => {
  return {
    tokenGetter: () => {
      if (storage) {
        // LoggerService.log('Storage is defined');
        return storage.get(STORAGE_CONST.AUTH_TOKEN);
      } else {
        /*LoggerService.log('Storage is not defined');*/
        return null;
      }
      // return localStorage.getItem(STORAGE_CONST.AUTH_TOKEN);
    },
    // allowedDomains: ['example.com']
  };
};

/*function initializeState() {
  return (): void => {

  };
}*/

/*export function fixPageFlickering(platformId: string): () => void {
  return () => {
    if (isPlatformBrowser(platformId)) {
      const transitionStyles = Array.from(
        document.querySelectorAll('style[ng-transition]')
      );

      const serverRoot = document.body.querySelector(
        'app-web-root'
      ) as HTMLElement;
      const clientRoot = serverRoot.cloneNode() as HTMLElement;

      serverRoot.setAttribute('ng-non-bindable', '');
      clientRoot.style.display = 'none';



      document.body.insertBefore(clientRoot, serverRoot);

      transitionStyles.forEach((element: HTMLElement) => {
        element.removeAttribute('ng-transition');
      });

      fromEvent(window, 'load').subscribe(() => {
        transitionStyles.forEach((el: HTMLElement) => el.remove());

        clientRoot.style.display = 'block';
        serverRoot.remove();
      });
    }
  };
}*/

function initializeState(appSettingsService: AppSettingsService) {
  return () => {
    return appSettingsService.getAppVersion().pipe(
      tap((result: AppVersionSetting) => {
        appSettingsService.appVersionSetting = result;
        appSettingsService.setAppCacheVersion(result);
        appSettingsService.verifyAppVersion(result);
        environment.t = result?.APP_SETTING_TITLE;
        environment.d = result?.APP_SETTING_DESCRIPTION;
        environment.m = result?.M_VERSION;
        environment.imgHost = result?.IMAGE_HOST
          ? result?.IMAGE_HOST
          : environment.imgHost;
      }),
      catchError((error) => {
        return of({});
      }),
    );
  };
}

export const GLOBAL_STATE_APP_INITIALIZER_PROVIDER = [
  {
    provide: APP_INITIALIZER,
    useFactory: initializeState,
    deps: [AppSettingsService],
    // useFactory: fixPageFlickering,
    // deps: [PLATFORM_ID],
    multi: true,
  },
];

export const WEB_APP_PROVIDERS = [
  {
    provide: APP_BASE_HREF,
    useValue: environment?.baseHref,
  },
  { provide: APP_ID, useValue: 'b2b-app' },
  importProvidersFrom([StorageUtilLibModule]),

  GLOBAL_STATE_APP_INITIALIZER_PROVIDER,
  provideRouter(
    [],
    // WEB_ROUTES,
    // withDebugTracing(),
    /**
     * **🚀 Perf Tip for TBT:**
     *
     * Disable initial sync navigation in router config and schedule it in router-outlet container component
     */
    // withDisabledInitialNavigation(),
    withComponentInputBinding(),
    withEnabledBlockingInitialNavigation(),
    withInMemoryScrolling({
      /**
       * **💡 UX Tip for InfiniteScroll:**
       *
       * Reset scroll position to top on route change, users could be
       * irritated starting a new list from the bottom of the page.
       *
       * also: otherwise infinite scroll isn't working properly
       */
      scrollPositionRestoration: 'enabled',
    }),
  ),
  {
    provide: ROUTES,
    useFactory: () => {
      const b2bDeviceUtilService = inject(B2bDeviceUtilService);
      return getWebRoutes(b2bDeviceUtilService);
    },
    multi: true,
  },
  provideHttpClient(
    withFetch(),
    withInterceptors([
      // BrowserStateInterceptor,
      HttpLoaderInterceptor,
      WebNoopHttpInterceptor,
    ]),
  ),
  provideClientHydration(
    withEventReplay(),
    withHttpTransferCacheOptions({
      includePostRequests: true,
      includeRequestsWithAuthHeaders: true,
      filter: (req) => true,
    }),
  ),
  provideAnimationsAsync(),
  // provideAnimations(),
  // provideState('root', reducers),
  provideStore(reducers, {
    runtimeChecks: {
      strictStateImmutability: true,
      strictActionImmutability: true,
    },
  }),
  provideEffects(effects),
  {
    provide: RouteReuseStrategy,
    useFactory: () => {
      const b2bDeviceUtilService = inject(B2bDeviceUtilService);
      if (b2bDeviceUtilService?.isDesktop()) {
        return new DefaultRouteReuseStrategy();
        // return null;
      } else {
        return new CustomRouteReuseStrategy();
        // return new IonicRouteStrategy();
      }
      // return getWebRoutes(b2bDeviceUtilService);
    },
    // useClass: CustomRouteReuseStrategy,
  },
  provideIonicAngular({
    backButtonText: '',
    backButtonIcon: 'arrow-back',
  }),
  /*{
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },*/
  /*  {
        provide: HTTP_INTERCEPTORS,
        useClass: BrowserStateInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: WebNoopHttpInterceptorService,
        multi: true,
      },*/
  { provide: 'WINDOW', useFactory: getWindow },
  { provide: 'NAVIGATOR', useFactory: getNavigator },
  GoogleTagManagerService,
  /**
   * **🚀 Perf Tip for LCP, TTI, TBT:**
   *
   * Disable zone.js as change detection system.
   * Add { ngZone: 'noop' } to the bootstrap options
   *
   * ⚠ Notice:
   * Don't forget to:
   * - remove `zone.js` import from the `polyfills.ts` file
   * - trigger change detection manually after NavigationEnd (or use the provided helper `ZonelessRouting`)
   *
   * 💡 Additional Optimization:
   * Remove the `polyfills` option from your `angular.json` to save 1 request and 118b
   *
   */
  // equivalent to `.bootstrapModule(AppModule, { ngZone: 'noop' })`
  /*{
      provide: NgZone,
      useClass: ɵNoopNgZone,
    },*/
  // RxActionFactory,
  // TMDB_HTTP_INTERCEPTORS_PROVIDER,
  /**
   * **🚀 Perf Tip for LCP, TTI:**
   *
   * Fetch data visible in viewport on app bootstrap instead of component initialization.
   */
  // GLOBAL_STATE_APP_INITIALIZER_PROVIDER,
  /**
   * **🚀 Perf Tip for TBT:**
   *
   * Chunk app bootstrap over APP_INITIALIZER.
   */
  // SCHEDULED_APP_INITIALIZER_PROVIDER,
  /**
   * **🚀 Perf Tip for TBT, LCP, CLS:**
   *
   * Configure RxAngular to get maximum performance.
   */
  // RXA_PROVIDER,
];
